import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  slideDuration: 300,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

// Both the main photo and the thumbnails wait on a network image, so both fade in rather than
// appearing in one frame. Only the main photo shimmers while it waits: it has a box of its own,
// reserved by the aspect ratio of the first photo. The thumbnail rail sizes itself to its photos
// (`.image-gallery-thumbnail { width: auto }`), so there is no box there to shimmer in.
//
// A photo that fails counts as an answer: waiting on it would leave the shimmer running forever.
// A photo in the server HTML can finish loading before React hydrates, and React does not replay
// that load event, so onLoad alone would leave it hidden. Check the <img> once on mount.
// ResponsiveImage takes no ref, hence the wrapper.
const useImageSettled = () => {
  const wrapperRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const img = wrapperRef.current?.querySelector('img');
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, []);
  return [wrapperRef, loaded, () => setLoaded(true)];
};

const GalleryPhoto = props => {
  const { item, variants, isFullscreen, aspectWidth, aspectHeight, imageSizesMaybe } = props;
  const [wrapperRef, loaded, stopWaiting] = useImageSettled();

  return (
    <AspectRatioWrapper
      width={aspectWidth || 1}
      height={aspectHeight || 1}
      className={classNames(isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper, {
        [css.shimmer]: !loaded,
      })}
    >
      <div className={css.itemCentering} ref={wrapperRef}>
        <ResponsiveImage
          rootClassName={css.item}
          className={classNames({ [css.imageLoading]: !loaded })}
          image={item.image}
          alt={item.alt}
          variants={variants}
          onLoad={stopWaiting}
          onError={stopWaiting}
          {...imageSizesMaybe}
        />
      </div>
    </AspectRatioWrapper>
  );
};

const GalleryThumb = props => {
  const { item, variants } = props;
  const [wrapperRef, loaded, stopWaiting] = useImageSettled();

  return (
    <div ref={wrapperRef}>
      <ResponsiveImage
        rootClassName={css.thumb}
        className={classNames({ [css.imageLoading]: !loaded })}
        image={item.image}
        alt={item.thumbAlt}
        variants={variants}
        sizes="88px"
        onLoad={stopWaiting}
        onError={stopWaiting}
      />
    </div>
  );
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  // The vertical thumbnail rail only makes sense next to the order column
  // (--viewportLarge). The viewport is unknowable during SSR, so this flips
  // after mount, the same way LayoutComposer resolves its responsive areas.
  const [isDesktopLayout, setIsDesktopLayout] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    // jsdom has no matchMedia
    if (!window.matchMedia) {
      return;
    }
    const mediaQueryList = window.matchMedia('(min-width: 1024px)');
    const onChange = e => setIsDesktopLayout(e.matches);
    setIsDesktopLayout(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', onChange);
    return () => mediaQueryList.removeEventListener('change', onChange);
  }, []);
  const { rootClassName, className, images, imageVariants, thumbnailVariants } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1342px) calc(100vw - 622px), 720px` };
  const renderItem = item => (
    <GalleryPhoto
      item={item}
      variants={imageVariants}
      isFullscreen={isFullscreen}
      aspectWidth={aspectWidth}
      aspectHeight={aspectHeight}
      imageSizesMaybe={imageSizesMaybe}
    />
  );
  const renderThumbInner = item => <GalleryThumb item={item} variants={thumbVariants} />;

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button
        type="button"
        className={css.navLeft}
        disabled={disabled}
        onClick={onClick}
        aria-label={intl.formatMessage({ id: 'ListingImageGallery.previous' })}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button
        type="button"
        className={css.navRight}
        disabled={disabled}
        onClick={onClick}
        aria-label={intl.formatMessage({ id: 'ListingImageGallery.next' })}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      additionalClass={classes}
      // Stack the thumbnails on the left of the main image on desktop, so the
      // gallery block fills the width toward the order column. Fullscreen mode
      // keeps the bottom strip, which its close/nav styling was designed for.
      thumbnailPosition={isDesktopLayout && !isFullscreen ? 'left' : 'bottom'}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

export default ListingImageGallery;
